/** Per-reference columns kept from a CMS reference report upload. */
export interface ReportDetails {
  claimedVideos: number | null;
  assetTitle: string;
  assetCustomId: string;
}

export interface ParsedReferenceReport {
  ids: string[];
  details: Map<string, ReportDetails>;
  hasClaimsColumn: boolean;
}

/** "Reference ID", "reference_id" and "referenceId" all normalise to "referenceid". */
const normaliseHeader = (value: unknown) =>
  String(value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const REFERENCE_HEADERS = ["referenceid", "reference", "refid"];
const CLAIMS_HEADERS = ["activeclaims", "claimedvideos", "activeclaimcount", "claims", "numclaims"];
const TITLE_HEADERS = ["assettitle", "title"];
const CUSTOM_ID_HEADERS = ["assetcustomid", "customid"];

// CMS downloads sometimes start with a few lines of report info before the header.
const HEADER_SEARCH_ROWS = 10;

const findColumn = (header: string[], names: string[]) => {
  for (const name of names) {
    const index = header.indexOf(name);
    if (index >= 0) return index;
  }
  return -1;
};

/**
 * Uses the column whose header looks like a reference ID column, else the first column.
 * When the file is a CMS reference report, also keeps active claims, asset title and custom ID.
 */
export const parseReferenceReport = (rows: string[][]): ParsedReferenceReport => {
  const details = new Map<string, ReportDetails>();
  const headerRow = rows
    .slice(0, HEADER_SEARCH_ROWS)
    .findIndex((row) => row.some((cell) => REFERENCE_HEADERS.includes(normaliseHeader(cell))));
  const header = headerRow >= 0 ? rows[headerRow].map(normaliseHeader) : [];
  const column = (names: string[]) => (headerRow >= 0 ? findColumn(header, names) : -1);

  const referenceIndex = headerRow >= 0 ? findColumn(header, REFERENCE_HEADERS) : 0;
  const claimsIndex = column(CLAIMS_HEADERS);
  const titleIndex = column(TITLE_HEADERS);
  const customIdIndex = column(CUSTOM_ID_HEADERS);
  const dataRows = headerRow >= 0 ? rows.slice(headerRow + 1) : rows;
  const cell = (row: string[], index: number) =>
    index >= 0 ? String(row[index] ?? "").trim() : "";

  const ids: string[] = [];
  for (const row of dataRows) {
    const id = cell(row, referenceIndex);
    if (!id || REFERENCE_HEADERS.includes(normaliseHeader(id))) continue;
    ids.push(id);
    if (claimsIndex >= 0 || titleIndex >= 0 || customIdIndex >= 0) {
      const claims = cell(row, claimsIndex).replace(/[,\s]/g, "");
      details.set(id, {
        claimedVideos: claims !== "" && !Number.isNaN(Number(claims)) ? Number(claims) : null,
        assetTitle: cell(row, titleIndex),
        assetCustomId: cell(row, customIdIndex),
      });
    }
  }
  return { ids, details, hasClaimsColumn: claimsIndex >= 0 };
};
